import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  createSavedFilter,
  deleteSavedFilter,
  fetchBacktest,
  fetchFilterableFields,
  fetchStocks,
  getDefaultView,
  listSavedFilters,
  setDefaultView,
  updateSavedFilter,
  type BacktestResponse,
  type ConditionGroup,
  type FilterableFieldDto,
  type FilterResponse,
  type SavedFilterDto,
  type SortSpec,
  type StockRow,
} from "../api";
import { ColumnsMenu } from "../components/ColumnsMenu";
import { exportRowsAsXlsx } from "../exportXlsx";
import { exportRowsAsCsv } from "../exportCsv";
import { FieldHelp } from "../components/FieldHelp";
import { FilterBuilder } from "../components/FilterBuilder";
import { PageHelper } from "../components/PageHelper";
import { SignalCell } from "../components/SignalCell";
import { AsOfBadge } from "../components/AsOfBadge";
import { buildStockColumns, type ColumnDef } from "../fields";

const DEFAULT_GROUPS: ConditionGroup[] = [{ op: "AND", conditions: [] }];
const PAGE_SIZES = [20, 50, 100, 200];
const TABLE_CONTEXT = "stocks";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type SortDir = "asc" | "desc";

// The handful of columns that are real named StockRow properties; every
// other column key (33 registered computed features) lives under
// row.features[key] instead -- one lookup point so sort/search/export
// never have to special-case "is this a feature column" separately.
const DEDICATED_KEYS = new Set([
  "security_id", "display_ticker", "sector_key", "cap_tier", "close",
  // DUAL-WEIGHT SIGNAL (2026-09-21): "signal" kept for back-compat (a
  // legacy sort-only alias, see stocks.py's _COVERAGE_FIELD_ATTR); the two
  // columns actually shown are signal_equal/signal_skill_weighted.
  "signal", "signal_equal", "signal_skill_weighted", "skill_delta",
  // Relative valuation band (2026-09-27, tiered 2026-09-28) -- daily-
  // cached, NULL for stocks with under 4 confirmed quarterly earnings
  // reports (66% of the universe has zero) or flagged by the EPS-source
  // cross-check, app/factory/stock_cape.py.
  "stock_relative_cape", "stock_cape_classification",
]);
const SIGNAL_KEYS = new Set(["signal_equal", "signal_skill_weighted"]);

// Tooltip on the Relative-valuation columns -- which tier/method produced
// this value, since tier1's CAPE multiple and tier3's sector-premium ratio
// are genuinely different metrics on different scales (see
// StockCapeChart.tsx's own tier handling for the full chart-side version).
const TIER_TITLE: Record<string, string> = {
  tier1: "10yr+ CAPE band (high confidence)",
  tier2: "5-10yr CAPE band (lower confidence)",
  tier3: "sector-premium band -- no CAPE possible, this stock's own P/E vs its sector's median (lowest confidence)",
};

function cellValue(row: StockRow, key: string): unknown {
  if (DEDICATED_KEYS.has(key)) return (row as unknown as Record<string, unknown>)[key];
  return row.features[key] ?? null;
}

export function StocksPage({
  token,
  onUnauthorized,
  onSelectStock,
}: {
  token: string;
  onUnauthorized: () => void;
  onSelectStock: (securityId: string) => void;
}) {
  const [mode, setMode] = useState<"live" | "backtest">("live");
  const [asOf, setAsOf] = useState(todayIso());
  // null = "model horizon" (default): each stock's own signal-matched
  // horizon, the pre-existing behavior. A number overrides the realized
  // window to that fixed trading-day count for every visible stock, so
  // differently-horizoned models can be compared on one common holding
  // period (2026-09-23).
  const [realizedHorizon, setRealizedHorizon] = useState<number | null>(null);
  const REALIZED_HORIZON_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];
  const [groups, setGroups] = useState<ConditionGroup[]>(DEFAULT_GROUPS);
  const [groupOp, setGroupOp] = useState<"AND" | "OR">("AND");
  const [outputType, setOutputType] = useState<"return" | "volatility">("return");
  // Seeded with the 5 dedicated columns; the feature defaults (+ any saved
  // default view) are applied once the registry/saved-view fetch resolves
  // below -- buildStockColumns([]) has no Technical entries yet at this point.
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    new Set(buildStockColumns([]).filter((c) => c.defaultVisible).map((c) => c.key)),
  );
  const [search, setSearch] = useState("");
  // Debounced 2026-09-22 alongside the server-side search fix -- `search`
  // updates the input instantly (so typing feels responsive), but the
  // actual refetch (now a real server round-trip, not a free client-side
  // filter) waits 300ms after the user stops typing so it isn't fired on
  // every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => {
      // Reset to page 1 in the SAME batch as the debounced value landing --
      // a search term changes what "page 1" even means (a differently-
      // matched set), same reasoning toggleSort already applies. Batched
      // together (not a separate effect keyed on debouncedSearch) so this
      // doesn't fire the fetch effect twice -- once for the new search
      // term at the old offset, then again when the offset reset lands.
      setDebouncedSearch(search);
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [fields, setFields] = useState<FilterableFieldDto[]>([]);
  const [savedFilters, setSavedFilters] = useState<SavedFilterDto[]>([]);
  // The strategy currently loaded into the builder (if any) -- lets "Save
  // strategy" offer an in-place update on the SAME filter_id instead of
  // always creating a new one.
  const [loadedFilter, setLoadedFilter] = useState<SavedFilterDto | null>(null);
  const [data, setData] = useState<FilterResponse | null>(null);
  const [backtest, setBacktest] = useState<BacktestResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  // Field registry + saved filters/default view -- fetched once at mount,
  // all from real per-user/live endpoints (no static mirrors, no localStorage).
  // Fields and the default view are resolved TOGETHER (Promise.all, not two
  // independent .then chains): buildStockColumns needs the live registry to
  // know which feature keys default-visible even applies to, and whichever
  // of the two requests happened to resolve second must not clobber the
  // other's result. Same race shape as the AddConditionForm fieldKey bug
  // fixed earlier this build -- fixed here by construction instead.
  useEffect(() => {
    listSavedFilters(token)
      .then(setSavedFilters)
      .catch((err) => {
        if (!handleAuthAwareError(err)) console.error(err);
      });
    Promise.all([fetchFilterableFields(token, "stock"), getDefaultView(token, TABLE_CONTEXT)])
      .then(([liveFields, view]) => {
        setFields(liveFields);
        if (view.columns && view.columns.length) {
          setVisibleCols(new Set(view.columns));
        } else {
          setVisibleCols(new Set(buildStockColumns(liveFields).filter((c) => c.defaultVisible).map((c) => c.key)));
        }
        if (view.sort_spec) {
          setSortKey(view.sort_spec.key);
          setSortDir(view.sort_spec.dir);
        }
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) console.error(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // The live, full column list (fields.ts's buildStockColumns) -- grows the
  // moment the registry does, no frontend change needed.
  const columns = useMemo(() => buildStockColumns(fields), [fields]);

  // Fields referenced by the ACTIVE filter tree are force-visible columns
  // (mock's "filter-referenced fields auto-added") -- computed from `groups`
  // directly rather than tracked as separate state, so it can never drift
  // out of sync with what's actually being filtered on (e.g. after loading
  // a saved strategy, or removing a condition).
  const forcedKeys = useMemo(() => {
    const s = new Set<string>();
    for (const g of groups) for (const c of g.conditions) s.add(c.field);
    return s;
  }, [groups]);

  const effectiveVisibleCols = useMemo(() => {
    return new Set([...visibleCols, ...forcedKeys]);
  }, [visibleCols, forcedKeys]);

  const displayedColumns = columns.filter((c) => effectiveVisibleCols.has(c.key));

  // A stable signature of which feature columns the request needs --
  // deliberately part of the auto-refetch trigger below (not just asOf).
  // BUG this closes: `fields` (and therefore `columns`/`displayedColumns`)
  // loads asynchronously AFTER first mount, so the mount-time fetch always
  // ran with extra_columns=[] (fields hadn't arrived yet). Nothing then
  // re-fetched once fields/visibleCols DID arrive, because neither was in
  // the effect's dependency list -- the table could sit showing empty
  // feature cells until asOf/outputType/limit/offset/mode happened to
  // change for an unrelated reason, which read as "changing the date
  // doesn't update the columns" from the outside even though the date
  // change itself worked; the REAL problem was the fetch immediately
  // before it never asked for those columns at all.
  const extraColumnsKey = displayedColumns
    .filter((c) => c.isFeature)
    .map((c) => c.key)
    .sort()
    .join(",");

  async function run() {
    setLoading(true);
    setError(null);
    try {
      const extraColumns = displayedColumns.filter((c) => c.isFeature).map((c) => c.key);
      const res = await fetchStocks(
        token,
        {
          groups,
          group_op: groupOp,
          include_signal: true,
          signal_output_type: outputType,
          extra_columns: extraColumns,
          // GLOBAL sort (2026-09-14 fix) -- sent to the backend so it sorts
          // the FULL matched set before slicing out this page, rather than
          // this page sorting only the ~50 rows it already has. `sortKey`
          // being any column that ISN'T already in extra_columns (a filter-
          // forced or Columns-menu-visible feature) still works: the
          // backend fetches whatever it's sorting on for every matched row
          // regardless of what's requested for display.
          sort_key: sortKey,
          sort_dir: sortDir,
          search: debouncedSearch.trim() || null,
          limit,
          offset,
        },
        asOf,
      );
      setData(res);
      if (mode === "backtest" && res.rows.length) {
        const bt = await fetchBacktest(token, res.rows.map((r) => r.security_id), asOf, outputType, realizedHorizon);
        setBacktest(bt);
      } else {
        setBacktest(null);
      }
    } catch (err) {
      if (handleAuthAwareError(err)) return;
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : "request failed");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  // Initial load + whenever the as-of date, output type, page, mode, OR the
  // set of feature columns actually being requested changes -- Block 3.2:
  // as-of applies to BOTH the filter and the signal. `extraColumnsKey` is
  // included specifically so the fields-registry load (and any column
  // visibility change) triggers a real refetch instead of leaving stale/
  // empty cells behind -- see the comment above `extraColumnsKey`.
  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOf, outputType, limit, offset, mode, extraColumnsKey, sortKey, sortDir, debouncedSearch, realizedHorizon]);

  // `data.rows` arrives ALREADY sorted AND searched globally by the backend
  // (see `run()` below) -- no client-side re-filter here anymore. FIXED
  // 2026-09-22: search used to be a client-side filter over only the
  // current page's ~50 rows -- confirmed live, searching "wipro" found
  // nothing because Wipro simply wasn't on the first page. Same class of
  // bug as the 2026-09-14 sort fix, same fix: search is now sent to the
  // backend and applied to the FULL matched set before pagination.
  const rows = data?.rows ?? [];

  const backtestBySid = useMemo(() => {
    const m = new Map<string, BacktestResponse["rows"][number]>();
    backtest?.rows.forEach((r) => m.set(r.security_id, r));
    return m;
  }, [backtest]);

  // REALIZED + MAX REALIZATION columns (backtest mode, return signals only):
  // two fixed, non-removable synthetic columns spliced into the visible list
  // right after the last signal column, so they read next to the prediction
  // they're scoring rather than off at the far right of the table. Sort is
  // PAGE-LOCAL, not the server-side global sort toggleSort drives -- both
  // values only exist for the rows already fetched (derived from a second,
  // per-page backtest call), so there's no server-side field to sort the
  // full matched set by.
  const REALIZED_COL_KEY = "backtest_realized";
  const MAX_REALIZED_COL_KEY = "backtest_max_realized";
  const showRealizedColumn = mode === "backtest" && outputType === "return";
  const columnsForRender = useMemo(() => {
    if (!showRealizedColumn) return displayedColumns;
    const lastSignalIdx = displayedColumns.reduce(
      (last, c, i) => (SIGNAL_KEYS.has(c.key) ? i : last),
      -1,
    );
    const insertAt = lastSignalIdx === -1 ? 0 : lastSignalIdx + 1;
    const realizedCol: ColumnDef = {
      key: REALIZED_COL_KEY, label: "Realized", group: "Model",
      defaultVisible: true, isFeature: false,
    };
    const maxRealizedCol: ColumnDef = {
      key: MAX_REALIZED_COL_KEY, label: "Max Realization", group: "Model",
      defaultVisible: true, isFeature: false,
    };
    const copy = [...displayedColumns];
    copy.splice(insertAt, 0, realizedCol, maxRealizedCol);
    return copy;
  }, [displayedColumns, showRealizedColumn]);

  // Page-local sort target: exactly ONE of these two columns can drive the
  // order at a time (clicking one clears the other), matching the main
  // table's own single-sortKey behavior rather than letting them stack.
  const [pageLocalSortKey, setPageLocalSortKey] = useState<typeof REALIZED_COL_KEY | typeof MAX_REALIZED_COL_KEY | null>(null);
  const [pageLocalSortDir, setPageLocalSortDir] = useState<SortDir>("desc");

  function togglePageLocalSort(key: typeof REALIZED_COL_KEY | typeof MAX_REALIZED_COL_KEY) {
    if (pageLocalSortKey === key) setPageLocalSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setPageLocalSortKey(key);
      setPageLocalSortDir("desc");
    }
  }

  function pageLocalValueFor(key: typeof REALIZED_COL_KEY | typeof MAX_REALIZED_COL_KEY, sid: string): number | null {
    const bt = backtestBySid.get(sid);
    return key === REALIZED_COL_KEY ? (bt?.signal_realized?.realized_value ?? null) : (bt?.max_realized?.value ?? null);
  }

  // Nulls/pending always sort last regardless of direction -- there's no
  // number to rank them by, and burying them at the top on a "desc" click
  // would read as if they scored the worst.
  const displayRows = useMemo(() => {
    if (!showRealizedColumn || !pageLocalSortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = pageLocalValueFor(pageLocalSortKey, a.security_id);
      const bv = pageLocalValueFor(pageLocalSortKey, b.security_id);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return pageLocalSortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, showRealizedColumn, pageLocalSortKey, pageLocalSortDir, backtestBySid]);

  // Live means "today" -- period. Rewinding a date only makes sense as a
  // deliberate backtest action; switching back to Live must not leave a
  // stale historical date silently governing what "live" shows.
  function switchMode(next: "live" | "backtest") {
    setMode(next);
    if (next === "live") setAsOf(todayIso());
  }

  function toggleSort(key: string) {
    // Sorting changes what "page 1" even means (it's now the global top of
    // a differently-ordered set), so stay on the CURRENT offset only if
    // that's still meaningful -- reset to the first page, matching how a
    // filter change already behaves, rather than showing an arbitrary
    // middle slice of the newly-sorted set.
    setOffset(0);
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function currentSortSpec(): SortSpec | null {
    return sortKey ? { key: sortKey, dir: sortDir } : null;
  }

  function exportCsv() {
    if (!data) return;
    const cols = displayedColumns.map((c) => c.key);
    const flatRows = rows.map((r) => Object.fromEntries(cols.map((c) => [c, cellValue(r, c)])));
    exportRowsAsCsv(flatRows, displayedColumns.map((c) => ({ key: c.key, label: c.label })), `stocks_${asOf}.csv`);
  }

  function exportXlsx() {
    if (!data) return;
    const cols = displayedColumns.map((c) => c.key);
    const flatRows = rows.map((r) => Object.fromEntries(cols.map((c) => [c, cellValue(r, c)])));
    exportRowsAsXlsx(flatRows, displayedColumns.map((c) => ({ key: c.key, label: c.label })), `stocks_${asOf}.xlsx`);
  }

  async function handleSaveDefaultView() {
    try {
      await setDefaultView(token, TABLE_CONTEXT, Array.from(visibleCols), currentSortSpec());
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "save failed");
    }
  }

  async function handleSaveStrategy(name: string) {
    try {
      const saved = await createSavedFilter(
        token,
        name,
        { groups, group_op: groupOp, signal_output_type: outputType },
        currentSortSpec(),
      );
      setSavedFilters((prev) => [saved, ...prev]);
      setLoadedFilter(saved);
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "save strategy failed");
    }
  }

  // Editing an EXISTING strategy (same filter_id) rather than always
  // creating a new one -- multiple strategies each cost real, non-trivial
  // compute in the monthly leaderboard, so "tweak and save" should refine
  // the one you loaded, not silently multiply how many get tracked.
  async function handleUpdateStrategy() {
    if (!loadedFilter) return;
    try {
      const saved = await updateSavedFilter(
        token,
        loadedFilter.filter_id,
        loadedFilter.name,
        { groups, group_op: groupOp, signal_output_type: outputType },
        currentSortSpec(),
      );
      setSavedFilters((prev) => prev.map((f) => (f.filter_id === saved.filter_id ? saved : f)));
      setLoadedFilter(saved);
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "update strategy failed");
    }
  }

  function handleLoadStrategy(f: SavedFilterDto) {
    setGroups(f.filter_tree.groups);
    setGroupOp(f.filter_tree.group_op);
    setOutputType(f.filter_tree.signal_output_type);
    if (f.sort_spec) {
      setSortKey(f.sort_spec.key);
      setSortDir(f.sort_spec.dir);
    }
    setOffset(0);
    setLoadedFilter(f);
  }

  async function handleDeleteStrategy(filterId: string) {
    try {
      await deleteSavedFilter(token, filterId);
      setSavedFilters((prev) => prev.filter((f) => f.filter_id !== filterId));
      if (loadedFilter?.filter_id === filterId) setLoadedFilter(null);
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "delete failed");
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Stocks</h1>
          <div className="page-sub">
            Screening <b>{data ? data.total_universe : "…"}</b> model-eligible stocks (NSE + BSE)
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div className="asof-control">
            <button
              className={`app-nav-tab${mode === "live" ? " active" : ""}`}
              style={{ padding: "5px 10px", fontSize: "0.75rem" }}
              onClick={() => switchMode("live")}
            >
              Live
            </button>
            <button
              className={`app-nav-tab${mode === "backtest" ? " active" : ""}`}
              style={{ padding: "5px 10px", fontSize: "0.75rem" }}
              onClick={() => switchMode("backtest")}
            >
              Backtest
            </button>
          </div>
          {mode === "live" ? (
            <div className="asof-control">
              <span className="asof-static">as of {todayIso()} · close</span>
            </div>
          ) : (
            <div className="asof-control">
              <label>Entry date (T)</label>
              <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} max={todayIso()} />
            </div>
          )}
        </div>
      </div>

      <PageHelper>
        Screen the model-eligible universe with a two-level filter, and see each stock's live <b>Signal</b> — the
        auto-assembled blend of its segment models' predictions. Switch to <b>Backtest</b> to rewind to a past date
        and see what the models actually predicted then, and (once resolved) what happened.
      </PageHelper>

      {mode === "backtest" && (
        <div className="banner show" style={{ display: "flex" }}>
          <span>◷</span>
          <span>
            Backtest reads resolved model predictions (<code>prediction_outcome</code>, data_spec S10.10) for each
            stock's contributing segment champion at this entry date. Phase 10's daily cron hasn't accumulated much
            history yet, so most rows will show <b>pending</b> — that's an honest empty state, not an error.
          </span>
        </div>
      )}

      {data && data.data_as_of && data.data_as_of < asOf && (
        <div className="banner show" style={{ display: "flex" }}>
          <span>⚠</span>
          <span>
            No bars/features/predictions exist past <b>{data.data_as_of}</b> yet (Phase 10's daily cron isn't running
            on a live schedule). Every date from {data.data_as_of} onward — including "Live" — resolves to that same
            last-available snapshot; this is the honest as-of-T fallback, not a stuck filter. Pick a date on or before{" "}
            {data.data_as_of} to see genuinely different values.
          </span>
        </div>
      )}

      <FilterBuilder
        groups={groups}
        groupOp={groupOp}
        fields={fields}
        token={token}
        asOf={asOf}
        savedFilters={savedFilters}
        onGroupsChange={setGroups}
        onGroupOpChange={setGroupOp}
        onAddGroup={() => setGroups([...groups, { op: "AND", conditions: [] }])}
        onRun={() => {
          setOffset(0);
          run();
        }}
        onClear={() => {
          setGroups(DEFAULT_GROUPS);
          setLoadedFilter(null);
        }}
        onSaveStrategy={handleSaveStrategy}
        onLoadStrategy={handleLoadStrategy}
        onDeleteStrategy={handleDeleteStrategy}
        onUpdateStrategy={handleUpdateStrategy}
        loadedFilter={loadedFilter}
        running={loading}
      />

      <section className="results-panel">
        <div className="results-bar">
          <div className="results-left">
            <span className="results-count">
              <b>{data ? data.matched : "…"}</b> matches
            </span>
            {data && <AsOfBadge date={data.data_as_of} reference={data.expected_as_of} label="bars/features" />}
            <div className="search-box">
              <span>⌕</span>
              <input placeholder="Search all matched stocks…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="search-box" style={{ minWidth: "auto" }}>
              <label style={{ color: "var(--ink-faint)", fontSize: "0.72rem", marginRight: 6 }}>Signal</label>
              <select
                value={outputType}
                onChange={(e) => setOutputType(e.target.value as "return" | "volatility")}
                style={{ border: 0, background: "transparent", font: "inherit" }}
              >
                <option value="return">Return</option>
                <option value="volatility">Volatility (rank-space)</option>
              </select>
              <FieldHelp term={outputType === "volatility" ? "rank_space_sigma" : "cross_segment_blend"} />
            </div>
          </div>
          <div className="results-tools">
            <ColumnsMenu
              columns={columns}
              visible={visibleCols}
              forcedKeys={forcedKeys}
              onChange={setVisibleCols}
              onSaveDefault={handleSaveDefaultView}
            />
            <div className="export-split">
              <button onClick={exportCsv} disabled={!data || rows.length === 0}>
                ↓ CSV
              </button>
              <button onClick={exportXlsx} disabled={!data || rows.length === 0}>
                Excel
              </button>
            </div>
          </div>
        </div>

        {error && <div className="error" style={{ margin: 14 }}>{error}</div>}

        {!error && data && data.matched === 0 && (
          <div className="empty-state">
            <div className="headline">No stocks match on {asOf}</div>
            <div>Per §3.8 this is an honest empty result, not an error — try widening the filter or a different date.</div>
            <div className="coverage-note">{data.coverage_report}</div>
          </div>
        )}

        {!error && data && data.matched > 0 && (
          <>
            <div className="table-wrap">
              <table className="stocks-table">
                <thead>
                  <tr>
                    {columnsForRender.map((c) => {
                      if (c.key !== REALIZED_COL_KEY && c.key !== MAX_REALIZED_COL_KEY) {
                        return (
                          <th
                            key={c.key}
                            className={c.key === "display_ticker" || c.key === "sector_key" ? "l" : SIGNAL_KEYS.has(c.key) ? "c" : undefined}
                            onClick={() => toggleSort(c.key)}
                          >
                            {c.label}
                            {SIGNAL_KEYS.has(c.key) && <FieldHelp term="cross_segment_blend" />}
                            {" "}
                            {sortKey === c.key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                          </th>
                        );
                      }
                      // Narrowed into a local so it survives capture in the
                      // onClick closure below -- TS drops property narrowing
                      // (c.key) across a new function scope.
                      const key: typeof REALIZED_COL_KEY | typeof MAX_REALIZED_COL_KEY = c.key;
                      return (
                        <th key={c.key} className="c">
                          <span style={{ cursor: "pointer" }} onClick={() => togglePageLocalSort(key)}>
                            {c.label}
                            <FieldHelp term={key === REALIZED_COL_KEY ? "horizon" : "max_realization"} />
                            {" "}
                            {pageLocalSortKey === key ? (pageLocalSortDir === "asc" ? "▲" : "▼") : "↕"}
                          </span>
                          {key === REALIZED_COL_KEY && (
                            <>
                              <br />
                              <select
                                value={realizedHorizon ?? "model"}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  setRealizedHorizon(e.target.value === "model" ? null : Number(e.target.value))
                                }
                                style={{ fontSize: "0.7rem", marginTop: 2 }}
                                title="Recompute both Realized and Max Realization at a fixed horizon for every visible stock, instead of each stock's own model-matched horizon"
                              >
                                <option value="model">model horizon</option>
                                {REALIZED_HORIZON_OPTIONS.map((h) => (
                                  <option key={h} value={h}>
                                    {h}d
                                  </option>
                                ))}
                              </select>
                            </>
                          )}
                        </th>
                      );
                    })}
                    {mode === "backtest" && <th className="l">Backtest outcome</th>}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row) => (
                    <tr key={row.security_id}>
                      {columnsForRender.map((c) =>
                        c.key === REALIZED_COL_KEY ? (
                          <td key={c.key} className="c">
                            <RealizedCell row={backtestBySid.get(row.security_id)} />
                          </td>
                        ) : c.key === MAX_REALIZED_COL_KEY ? (
                          <td key={c.key} className="c">
                            <MaxRealizedCell row={backtestBySid.get(row.security_id)} />
                          </td>
                        ) : (
                          <td
                            key={c.key}
                            className={c.key === "display_ticker" || c.key === "sector_key" ? "l" : SIGNAL_KEYS.has(c.key) ? "c" : "num"}
                          >
                            {renderCell(
                              row,
                              c,
                              outputType,
                              onSelectStock,
                              // The "signal as of X, stale" badge compares a row's
                              // signal date against TODAY's real data frontier --
                              // meaningless in Backtest mode, where every row is
                              // deliberately resolved as of a past entry date and
                              // will always differ from today. Suppressed there by
                              // passing null (SignalCell's own gate is `dataAsOf
                              // != null`), rather than hiding it with CSS, so it
                              // costs nothing extra to reason about later.
                              mode === "live" ? (data?.data_as_of ?? null) : null,
                            )}
                          </td>
                        ),
                      )}
                      {mode === "backtest" && (
                        <td className="l">
                          <BacktestCell row={backtestBySid.get(row.security_id)} outputType={outputType} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pager">
              <label>
                Page size{" "}
                <select value={limit} onChange={(e) => { setOffset(0); setLimit(Number(e.target.value)); }}>
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <button className="btn ghost sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
                ← Prev
              </button>
              <span>
                {offset + 1}–{offset + data.rows.length} of {data.matched}
              </span>
              <button
                className="btn ghost sm"
                disabled={offset + limit >= data.matched}
                onClick={() => setOffset(offset + limit)}
              >
                Next →
              </button>
            </div>
          </>
        )}

        <div className="foot-note">
          Signal = cross-segment coverage blend (<span className="k">S10.13</span>), as of {asOf}. No confidence arc is
          shown: the cross-segment blend never reports agreement/dispersion by design (that belongs to within-slot
          ensembles only). Hover a gaps badge for which segments didn't contribute and why.
        </div>
      </section>
    </>
  );
}

/** Just the realized number, colored -- horizon is already shown on the
 * signal column right next to this one (signal_horizon), so repeating it
 * here would be redundant. "pending" is the only text, for the honest
 * not-yet-resolved case (data_spec S10.10). */
function RealizedCell({
  row,
}: {
  row: { signal_realized: { resolved: boolean; realized_value: number | null } | null } | undefined;
}) {
  const sr = row?.signal_realized;
  if (!sr) {
    return <span className="mut">—</span>;
  }
  if (!sr.resolved) {
    return <span className="pending mut">pending</span>;
  }
  const v = sr.realized_value!;
  return (
    <span className={`num ${v >= 0 ? "pos" : "neg"}`}>
      {v >= 0 ? "+" : ""}
      {(v * 100).toFixed(2)}%
    </span>
  );
}

/** The best return reached anywhere in the same window RealizedCell scores
 * (2026-09-23) -- "best case if you'd sold at the peak," not the value
 * sitting at the window's end. A trailing "*" (with a tooltip, not a wall
 * of text in the cell) marks a PARTIAL read: the horizon hasn't fully
 * elapsed yet, so this is the best point reached SO FAR, not necessarily
 * the eventual peak once the full window resolves. */
function MaxRealizedCell({
  row,
}: {
  row: { max_realized: { value: number; partial: boolean } | null } | undefined;
}) {
  const mr = row?.max_realized;
  if (!mr) {
    return <span className="mut">—</span>;
  }
  return (
    <span className={`num ${mr.value >= 0 ? "pos" : "neg"}`}>
      {mr.value >= 0 ? "+" : ""}
      {(mr.value * 100).toFixed(2)}%
      {mr.partial && (
        <span className="mut" title="Partial: the horizon hasn't fully elapsed yet -- this is the best point reached so far, not necessarily the eventual peak">
          *
        </span>
      )}
    </span>
  );
}

function BacktestCell({
  row,
  outputType,
}: {
  row: { segments: { segment_key: string; predicted_value: number; resolved: boolean; realized_value: number | null; error: number | null; resolve_date: string }[] } | undefined;
  outputType: "return" | "volatility";
}) {
  if (!row || row.segments.length === 0) {
    return <span className="mut">no prediction on this entry date</span>;
  }
  const fmt = (v: number) => (outputType === "volatility" ? v.toFixed(4) : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`);
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {row.segments.map((s) => (
        <span key={s.segment_key} className="mut" style={{ fontSize: "0.78rem" }}>
          <span className="chip">{s.segment_key}</span>{" "}
          <span className="num">predicted {fmt(s.predicted_value)}</span>{" "}
          {s.resolved ? (
            <span className="num pos">→ realized {fmt(s.realized_value!)}</span>
          ) : (
            <span className="pending" style={{ fontFamily: "var(--font-mono)" }}>
              pending (resolves {s.resolve_date})
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

function renderCell(
  row: StockRow,
  column: ColumnDef,
  outputType: "return" | "volatility",
  onSelectStock: (securityId: string) => void,
  dataAsOf: string | null,
) {
  if (column.isFeature) {
    // ANY registered computed feature (33 as of the 2026-09-02 seeding) --
    // generic, not one hardcoded case per feature. Read from the
    // response's features bag; "—" is either "not fetched this request"
    // (not in extra_columns) or a genuine warm-up/coverage null -- both
    // honest absences, not worth distinguishing in a table cell (the
    // gaps badge pattern on the signal column is reserved for the
    // coverage-blend caveat specifically).
    const v = row.features[column.key];
    return v !== null && v !== undefined ? v.toFixed(4) : "—";
  }
  switch (column.key) {
    case "signal_equal":
      return <SignalCell row={row} outputType={outputType} dataAsOf={dataAsOf} variant="equal" />;
    case "signal_skill_weighted":
      return <SignalCell row={row} outputType={outputType} dataAsOf={dataAsOf} variant="skill" />;
    case "display_ticker":
      return (
        <span className="tk tk-link" onClick={() => onSelectStock(row.security_id)} title="Open instrument detail">
          {row.display_ticker ?? row.security_id}
        </span>
      );
    case "sector_key":
      // Dimension prefix ("sector:") dropped from display -- the column
      // header already says SECTOR, so repeating it in every cell wasted
      // horizontal space. Full segment_key kept in the title tooltip in
      // case the raw value ever matters (e.g. matching a filter condition
      // string exactly).
      return row.sector_key ? <span className="chip" title={row.sector_key}>{row.sector_key.split(":")[1] ?? row.sector_key}</span> : <span className="mut">—</span>;
    case "cap_tier":
      return row.cap_tier ? <span className="chip" title={row.cap_tier}>{row.cap_tier.split(":")[1] ?? row.cap_tier}</span> : <span className="mut">—</span>;
    case "close":
      return row.close !== null ? row.close.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—";
    case "stock_relative_cape":
      return row.stock_relative_cape !== null
        ? <span title={TIER_TITLE[row.stock_cape_tier ?? ""] ?? ""}>{row.stock_relative_cape.toFixed(row.stock_cape_tier === "tier3" ? 2 : 1)}×</span>
        : "—";
    case "stock_cape_classification":
      return row.stock_cape_classification
        ? <span className={`valuation-chip ${row.stock_cape_classification}`} title={TIER_TITLE[row.stock_cape_tier ?? ""] ?? ""}>{row.stock_cape_classification}</span>
        : <span className="mut">—</span>;
    default:
      return "—";
  }
}
